import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import api from '@/services/api';
import { logError } from '@/utils/errorHandler';
import { ManageLocalesModal } from './ManageLocalesModal';
import { ManageChannelsModal } from './ManageChannelsModal';
import { ManageCurrenciesModal } from './ManageCurrenciesModal';
import { ManagePriceTypesModal } from './ManagePriceTypesModal';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';

export function OrganizationSettingsForm() {
  // Get translations
  const i18n = useSettingspageTranslations();

  const orgSettingsSchema = z.object({
    name: z.string().min(1, { message: i18n.validation.organizationNameRequired }),
    default_locale: z.string().min(1, { message: i18n.validation.defaultLocaleRequired }),
    default_channel: z.string().optional(),
    default_currency: z.string().optional(),
    default_price_type: z.string().optional(),
    number_format: z.enum(['dot', 'comma'], {
      errorMap: () => ({ message: i18n.validation.numberFormatRequired }),
    }),
    date_format: z.enum(['MDY', 'DMY', 'YMD'], {
      errorMap: () => ({ message: i18n.validation.dateFormatRequired }),
    }),
  });

  type OrgSettingsFormValues = z.infer<typeof orgSettingsSchema>;

  const { user } = useAuth();
  const orgId = user?.organization_id;
  const {
    orgSettings,
    isLoading,
    error,
    refetch,
    locales,
    channels,
    isChannelsLoading,
    currencies,
    priceTypes,
    defaultCurrency,
    defaultPriceType,
  } = useOrgSettings();
  const queryClient = useQueryClient();

  const form = useForm<OrgSettingsFormValues>({
    resolver: zodResolver(orgSettingsSchema),
    defaultValues: {
      name: orgSettings?.name || '',
      default_locale: orgSettings?.default_locale || 'en_US',
      default_channel: orgSettings?.default_channel?.id?.toString() || 'none',
      default_currency: defaultCurrency?.iso_code || 'none',
      default_price_type: defaultPriceType?.id?.toString() || 'none',
      number_format: orgSettings?.number_format || 'dot',
      date_format: orgSettings?.date_format || 'MDY',
    },
  });

  // Update form values when orgSettings loads
  React.useEffect(() => {
    if (orgSettings) {
      form.reset({
        name: orgSettings.name,
        default_locale: orgSettings.default_locale,
        default_channel: orgSettings.default_channel?.id?.toString() || 'none',
        default_currency: defaultCurrency?.iso_code || 'none',
        default_price_type: defaultPriceType?.id?.toString() || 'none',
        number_format: orgSettings.number_format || 'dot',
        date_format: orgSettings.date_format || 'MDY',
      });
    }
  }, [orgSettings, form, defaultCurrency, defaultPriceType]);

  const { mutate: updateOrgSettings, isPending } = useMutation({
    mutationFn: async (data: OrgSettingsFormValues) => {
      if (!orgId) throw new Error('No organization ID available');
      // Find the locale object by code
      const selectedLocaleObj = locales.find(l => l.code === data.default_locale);
      // Convert channel ID to number if provided and not 'none'
      const payload: any = {
        name: data.name,
        default_locale: selectedLocaleObj ? selectedLocaleObj.code : data.default_locale,
        number_format: data.number_format,
        date_format: data.date_format,
      };
      if (data.default_channel && data.default_channel !== 'none') {
        payload.default_channel_id = parseInt(data.default_channel);
      } else {
        payload.default_channel_id = null;
      }

      // Handle default currency
      if (data.default_currency && data.default_currency !== 'none') {
        payload.default_currency_id = data.default_currency;
      } else {
        payload.default_currency_id = null;
      }

      // Handle default price type
      if (data.default_price_type && data.default_price_type !== 'none') {
        payload.default_price_type_id = parseInt(data.default_price_type);
      } else {
        payload.default_price_type_id = null;
      }

      const response = await api.patch(`/api/organizations/${orgId}/`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success(i18n.organization.successMessage);
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
      refetch();
    },
    onError: error => {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'OrganizationSettingsForm',
        action: 'updateOrgSettings',
        metadata: {
          orgId,
          context: 'organization_settings_update_failure',
        },
      });
      toast.error(i18n.errors.loadOrganization);
    },
  });

  function onSubmit(data: OrgSettingsFormValues) {
    updateOrgSettings(data);
  }

  // Modal state
  const [isManageLocalesModalOpen, setManageLocalesModalOpen] = React.useState(false);
  const [isManageChannelsModalOpen, setManageChannelsModalOpen] = React.useState(false);
  const [isCurrenciesModalOpen, setCurrenciesModalOpen] = React.useState(false);
  const [isPriceTypesModalOpen, setPriceTypesModalOpen] = React.useState(false);

  if (isLoading || isChannelsLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="py-4 text-center text-destructive">{i18n.organization.errorMessage}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{i18n.organization.title}</CardTitle>
        <CardDescription>{i18n.organization.description}</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{i18n.organization.nameLabel}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Default Locale Dropdown + Add New */}
            <FormField
              control={form.control}
              name="default_locale"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{i18n.organization.defaultLocaleLabel}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={i18n.placeholders.selectDefaultLocale} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {locales
                        .filter(locale => locale.code && locale.code.trim() !== '')
                        .map(locale => (
                          <SelectItem key={locale.code} value={locale.code}>
                            {locale.name} ({locale.code})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setManageLocalesModalOpen(true)}
                    >
                      {i18n.organization.manageButton}
                    </Button>
                  </div>
                  <FormDescription>{i18n.organization.localeDescription}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Default Channel */}
            <FormField
              control={form.control}
              name="default_channel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{i18n.organization.defaultChannelLabel}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">{i18n.organization.options.none}</SelectItem>
                      {channels
                        .filter(channel => channel.id && channel.name && channel.name.trim() !== '')
                        .map(channel => (
                          <SelectItem key={channel.id} value={channel.id.toString()}>
                            {channel.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setManageChannelsModalOpen(true)}
                    >
                      {i18n.organization.manageButton}
                    </Button>
                  </div>
                  <FormDescription>{i18n.organization.channelDescription}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Default Currency */}
            <FormField
              control={form.control}
              name="default_currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{i18n.organization.defaultCurrencyLabel}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={i18n.placeholders.selectDefaultCurrency} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">{i18n.organization.options.none}</SelectItem>
                      {currencies
                        .filter(currency => currency.iso_code && currency.iso_code.trim() !== '')
                        .map(currency => (
                          <SelectItem key={currency.iso_code} value={currency.iso_code}>
                            {currency.iso_code} - {currency.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrenciesModalOpen(true)}
                    >
                      {i18n.organization.manageButton}
                    </Button>
                  </div>
                  <FormDescription>{i18n.organization.currencyDescription}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Default Price Type */}
            <FormField
              control={form.control}
              name="default_price_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{i18n.organization.defaultPriceTypeLabel}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">{i18n.organization.options.none}</SelectItem>
                      {priceTypes
                        .filter(
                          priceType =>
                            priceType.id && priceType.label && priceType.label.trim() !== ''
                        )
                        .map(priceType => (
                          <SelectItem key={priceType.id} value={priceType.id.toString()}>
                            {priceType.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPriceTypesModalOpen(true)}
                    >
                      {i18n.organization.manageButton}
                    </Button>
                  </div>
                  <FormDescription>{i18n.organization.priceTypeDescription}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Number Format */}
            <FormField
              control={form.control}
              name="number_format"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{i18n.organization.numberFormatLabel}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={i18n.placeholders.selectNumberFormat} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="dot">
                        {i18n.organization.options.numberFormats.dot}
                      </SelectItem>
                      <SelectItem value="comma">
                        {i18n.organization.options.numberFormats.comma}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>{i18n.organization.numberFormatDescription}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date Format */}
            <FormField
              control={form.control}
              name="date_format"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{i18n.organization.dateFormatLabel}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={i18n.placeholders.selectDateFormat} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="MDY">
                        {i18n.organization.options.dateFormats.MDY}
                      </SelectItem>
                      <SelectItem value="DMY">
                        {i18n.organization.options.dateFormats.DMY}
                      </SelectItem>
                      <SelectItem value="YMD">
                        {i18n.organization.options.dateFormats.YMD}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>{i18n.organization.dateFormatDescription}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              disabled={isPending}
              className="bg-teal-600 text-white hover:bg-teal-700"
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {i18n.organization.savingLabel}
                </>
              ) : (
                i18n.organization.updateButton
              )}
            </Button>
          </CardFooter>
        </form>
      </Form>

      {/* Modals */}
      <ManageLocalesModal
        open={isManageLocalesModalOpen}
        onClose={() => setManageLocalesModalOpen(false)}
      />
      <ManageChannelsModal
        open={isManageChannelsModalOpen}
        onClose={() => setManageChannelsModalOpen(false)}
      />
      <ManageCurrenciesModal
        open={isCurrenciesModalOpen}
        onClose={() => setCurrenciesModalOpen(false)}
      />
      <ManagePriceTypesModal
        open={isPriceTypesModalOpen}
        onClose={() => setPriceTypesModalOpen(false)}
        onSuccess={() => {
          refetch(); // Trigger useOrgSettings refetch to update price types dropdown
        }}
      />
    </Card>
  );
}
